import { useState } from 'react';
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Eye, Shield, Users, BookOpen, CheckCircle, Target, TrendingUp,
  Briefcase, Layers, Crown, Mail, AlertTriangle, Sparkles, Lightbulb, UserCheck, Brain,
  Calendar, Clock, Zap, ArrowRight, CheckCircle2, FileText, PlayCircle
} from 'lucide-react';
import {
  equipeStrategia,
  equipeBZ,
  activitesRACI,
  philosophieGouvernance,
  statsGouvernance,
  exempleConcret,
  piegesRACI,
  engagementBZ,
  beneficesRACI
} from '../data/gouvernanceComiteData';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface GouvernanceComiteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GouvernanceComiteModal: React.FC<GouvernanceComiteModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('presentation');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['gouvernance_comite_ti_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const tabs = [
    { id: 'presentation', label: 'Étape #1 PRIsME', icon: Sparkles },
    { id: 'raison', label: 'Raison d\'être', icon: Target },
    { id: 'reunions', label: 'Gestion Réunions', icon: Calendar },
    { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
    { id: 'raci', label: 'Matrice RACI', icon: Shield },
    { id: 'equipe', label: 'Équipe & Rôles', icon: Users },
    { id: 'guide', label: 'Guide Pédagogique', icon: BookOpen }
  ];

  // Helper pour le badge RACI
  const getRACIBadge = (lettre: 'R' | 'A' | 'C' | 'I') => {
    const config = {
      R: { bg: 'bg-blue-500', label: 'Responsible' },
      A: { bg: 'bg-green-500', label: 'Accountable' },
      C: { bg: 'bg-orange-500', label: 'Consulted' },
      I: { bg: 'bg-purple-500', label: 'Informed' }
    };
    return (
      <div className={`${config[lettre].bg} text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg shadow-md`}>
        {lettre}
      </div>
    );
  };

  // Grouper activités par niveau
  const activitesParNiveau = {
    Stratégique: activitesRACI.filter(a => a.niveau === 'Stratégique'),
    Tactique: activitesRACI.filter(a => a.niveau === 'Tactique'),
    Opérationnel: activitesRACI.filter(a => a.niveau === 'Opérationnel')
  };

  const couleurNiveau = {
    Stratégique: 'from-purple-500 to-pink-500',
    Tactique: 'from-blue-500 to-cyan-500',
    Opérationnel: 'from-green-500 to-emerald-500'
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 p-8">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-6">
              <div className="bg-white/20 backdrop-blur-xl p-5 rounded-2xl">
                <Shield className="w-12 h-12 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-4xl font-black text-white">Gouvernance du Comité TI</h1>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-purple-100 text-lg font-semibold">Matrice RACI • Transparence • Autonomie décisionnelle</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white border-b border-gray-200 px-8">
            <div className="flex gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 flex items-center gap-2 font-semibold transition-all relative ${
                      activeTab === tab.id
                        ? 'text-purple-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-8">
            {/* ÉTAPE #1 PRIsME */}
            {activeTab === 'presentation' && (
              <div className="space-y-8">
                {/* Hero Section */}
                <div className="text-center mb-8">
                  <div className="inline-block bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 p-6 rounded-3xl mb-6 shadow-2xl">
                    <Sparkles className="w-20 h-20 text-white" />
                  </div>
                  <h2 className="text-5xl font-black text-gray-900 mb-4">
                    Étape #1 : Gouvernance du Comité TI
                  </h2>
                  <p className="text-2xl text-gray-700 max-w-4xl mx-auto font-semibold">
                    Le premier sujet à discuter lors de la présentation du PRIsME à un nouveau client
                  </p>
                </div>

                {/* Pourquoi c'est l'étape #1 */}
                <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100 border-4 border-purple-400 rounded-3xl p-8 shadow-xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-purple-600 rounded-full p-4">
                      <Target className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-900">Pourquoi commencer par la gouvernance?</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white w-12 h-12 rounded-xl flex items-center justify-center font-black text-xl">1</div>
                        <h4 className="font-bold text-xl text-gray-900">Fondation de tout le reste</h4>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Sans gouvernance claire, aucun projet TI ne peut être efficacement piloté. C'est la <strong>base sur laquelle tout le PRIsME s'appuie</strong>.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white w-12 h-12 rounded-xl flex items-center justify-center font-black text-xl">2</div>
                        <h4 className="font-bold text-xl text-gray-900">Confiance et transparence</h4>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Montrer une gouvernance structurée dès le départ <strong>démontre notre professionnalisme</strong> et rassure le client sur notre approche méthodique.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white w-12 h-12 rounded-xl flex items-center justify-center font-black text-xl">3</div>
                        <h4 className="font-bold text-xl text-gray-900">Clarification des rôles</h4>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Le client comprend <strong>qui fait quoi, qui décide quoi</strong>. Cela évite les malentendus et accélère les décisions.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white w-12 h-12 rounded-xl flex items-center justify-center font-black text-xl">4</div>
                        <h4 className="font-bold text-xl text-gray-900">Cadre de travail</h4>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Une fois la gouvernance établie, <strong>tous les autres éléments du PRIsME</strong> (projets, budgets, KPIs) s'intègrent naturellement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notre Approche Méthodique */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-4 border-blue-300 rounded-3xl p-8 shadow-xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-blue-600 rounded-full p-4">
                      <Zap className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-900">Notre Approche Méthodique et Rodée</h3>
                  </div>
                  <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                    Chez BZ, nous ne faisons pas de la gouvernance "au feeling". Nous avons une <strong>approche structurée, éprouvée et toujours méthodique</strong> qui garantit l'excellence à chaque réunion.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white rounded-2xl p-6 border-2 border-blue-300">
                      <div className="flex items-center gap-3 mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                        <h4 className="font-bold text-lg text-gray-900">Préparation systématique</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Chaque réunion est <strong>préparée en amont</strong> : ordre du jour structuré, documents pré-chargés, KPIs à jour, actions du comité précédent suivies.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border-2 border-blue-300">
                      <div className="flex items-center gap-3 mb-4">
                        <Clock className="w-8 h-8 text-blue-600" />
                        <h4 className="font-bold text-lg text-gray-900">Cadence respectée</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Réunions trimestrielles</strong> (Q1, Q2, Q3, Q4) toujours respectées. Durée optimisée (90-120 min) pour efficacité maximale.
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border-2 border-blue-300">
                      <div className="flex items-center gap-3 mb-4">
                        <FileText className="w-8 h-8 text-purple-600" />
                        <h4 className="font-bold text-lg text-gray-900">Documentation complète</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Procès-verbaux systématiques</strong> : décisions tracées, actions assignées (RACI), échéances définies. Rien ne se perd.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <PlayCircle className="w-8 h-8" />
                      <h4 className="font-bold text-xl">Comment on fonctionne à chaque réunion</h4>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <ArrowRight className="w-6 h-6 flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-lg">T+0 (Avant la réunion) :</strong>
                          <p className="text-green-100">Ordre du jour envoyé 48h avant, documents partagés, rappel automatique aux participants.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <ArrowRight className="w-6 h-6 flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-lg">Pendant la réunion :</strong>
                          <p className="text-green-100">Structure claire : KPIs (15 min), Projets en cours (30 min), Décisions stratégiques (30 min), Risques & Budget (15 min), Actions & Suivi (15 min).</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <ArrowRight className="w-6 h-6 flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-lg">T+24h (Après la réunion) :</strong>
                          <p className="text-green-100">Procès-verbal distribué avec décisions, actions assignées (RACI), échéances, prochaine réunion planifiée.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <ArrowRight className="w-6 h-6 flex-shrink-0 mt-1" />
                        <div>
                          <strong className="text-lg">Entre les réunions :</strong>
                          <p className="text-green-100">Suivi continu des actions, escalades si nécessaire, préparation de la prochaine réunion.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message Clé */}
                <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white rounded-3xl p-8 text-center shadow-2xl">
                  <div className="text-6xl mb-4">🎯</div>
                  <blockquote className="text-3xl font-black italic mb-4 leading-relaxed">
                    "La gouvernance du Comité TI n'est pas une option, c'est la fondation. Sans elle, le PRIsME ne peut pas fonctionner. C'est pourquoi c'est toujours notre première discussion avec un nouveau client."
                  </blockquote>
                  <p className="text-purple-100 text-xl font-semibold">— Approche BZ : Méthodique, Rodée, Toujours Excellente</p>
                </div>
              </div>
            )}

            {/* GESTION DES RÉUNIONS */}
            {activeTab === 'reunions' && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <div className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 p-6 rounded-3xl mb-6 shadow-2xl">
                    <Calendar className="w-20 h-20 text-white" />
                  </div>
                  <h2 className="text-5xl font-black text-gray-900 mb-4">
                    Gestion des Réunions : Notre Méthodologie Rodée
                  </h2>
                  <p className="text-2xl text-gray-700 max-w-4xl mx-auto font-semibold">
                    Comment nous fonctionnons à chaque réunion pour garantir l'excellence
                  </p>
                </div>

                {/* Workflow Complet */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-4 border-blue-300 rounded-3xl p-8 shadow-xl">
                  <h3 className="text-3xl font-black text-gray-900 mb-6 flex items-center gap-4">
                    <Clock className="w-10 h-10 text-blue-600" />
                    Workflow Complet : De la Préparation au Suivi
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Phase 1 */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-blue-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-blue-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl">1</div>
                        <h4 className="text-2xl font-bold text-gray-900">T-48h : Préparation</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Ordre du jour structuré</strong>
                            <p className="text-gray-700 text-sm">KPIs, Projets, Décisions, Risques, Budget, Actions</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Documents pré-chargés</strong>
                            <p className="text-gray-700 text-sm">Rapports, présentations, métriques à jour</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Rappel automatique</strong>
                            <p className="text-gray-700 text-sm">Invitation calendrier + email de rappel</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Suivi actions précédentes</strong>
                            <p className="text-gray-700 text-sm">État d'avancement des actions du comité précédent</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phase 2 */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-green-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-green-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl">2</div>
                        <h4 className="text-2xl font-bold text-gray-900">T+0 : Pendant la Réunion (90-120 min)</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-green-600" />
                            <strong className="text-gray-900">KPIs (15 min)</strong>
                          </div>
                          <p className="text-gray-700 text-sm">Performance opérationnelle, SLA, incidents, satisfaction</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Briefcase className="w-5 h-5 text-green-600" />
                            <strong className="text-gray-900">Projets en cours (30 min)</strong>
                          </div>
                          <p className="text-gray-700 text-sm">État d'avancement, blocages, décisions requises</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-5 h-5 text-green-600" />
                            <strong className="text-gray-900">Décisions stratégiques (30 min)</strong>
                          </div>
                          <p className="text-gray-700 text-sm">Nouveaux projets, priorités, arbitrages</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-green-600" />
                            <strong className="text-gray-900">Risques & Budget (15 min)</strong>
                          </div>
                          <p className="text-gray-700 text-sm">Identification risques, suivi budget, ajustements</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <strong className="text-gray-900">Actions & Suivi (15 min)</strong>
                          </div>
                          <p className="text-gray-700 text-sm">Assignation actions (RACI), échéances, prochaine réunion</p>
                        </div>
                      </div>
                    </div>

                    {/* Phase 3 */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-purple-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl">3</div>
                        <h4 className="text-2xl font-bold text-gray-900">T+24h : Procès-Verbal</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Décisions documentées</strong>
                            <p className="text-gray-700 text-sm">Qui a décidé quoi, quand, pourquoi</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Users className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Actions assignées (RACI)</strong>
                            <p className="text-gray-700 text-sm">Responsable, Accountable, Consulted, Informed</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Calendar className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Échéances définies</strong>
                            <p className="text-gray-700 text-sm">Dates limites claires pour chaque action</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <ArrowRight className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                          <div>
                            <strong className="text-gray-900">Prochaine réunion planifiée</strong>
                            <p className="text-gray-700 text-sm">Date, heure, ordre du jour préliminaire</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phase 4 */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-orange-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-orange-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-black text-2xl">4</div>
                        <h4 className="text-2xl font-bold text-gray-900">Entre les Réunions : Suivi Continu</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                          <strong className="text-gray-900">Suivi des actions</strong>
                          <p className="text-gray-700 text-sm">Mise à jour régulière de l'état d'avancement</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                          <strong className="text-gray-900">Escalades si nécessaire</strong>
                          <p className="text-gray-700 text-sm">Blocages identifiés et résolus rapidement</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                          <strong className="text-gray-900">Préparation prochaine réunion</strong>
                          <p className="text-gray-700 text-sm">Collecte données, préparation documents</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pourquoi c'est Rodé */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-3xl p-8 shadow-2xl">
                  <h3 className="text-3xl font-black mb-6 flex items-center gap-4">
                    <Zap className="w-10 h-10" />
                    Pourquoi Notre Approche est Rodée
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                      <h4 className="font-bold text-xl mb-3">Expérience accumulée</h4>
                      <p className="text-indigo-100">Des centaines de réunions de comité TI gérées avec succès. Chaque réunion nous rend meilleurs.</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                      <h4 className="font-bold text-xl mb-3">Processus standardisé</h4>
                      <p className="text-indigo-100">Même structure, même cadence, mêmes livrables. La répétition crée l'excellence.</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                      <h4 className="font-bold text-xl mb-3">Outils optimisés</h4>
                      <p className="text-indigo-100">Templates, checklists, automatisations. Tout est pensé pour minimiser l'effort et maximiser la qualité.</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                      <h4 className="font-bold text-xl mb-3">Amélioration continue</h4>
                      <p className="text-indigo-100">Chaque réunion génère des apprentissages. Nous ajustons constamment pour être meilleurs.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* RAISON D'ÊTRE */}
            {activeTab === 'raison' && (
              <div className="space-y-8">
                {/* Titre principal */}
                <div className="text-center mb-8">
                  <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-2xl mb-4">
                    <Target className="w-16 h-16 text-white" />
                  </div>
                  <h2 className="text-4xl font-black text-gray-900 mb-3">
                    Pourquoi un Comité TI?
                  </h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    La gouvernance technologique au service de la vision stratégique d'affaires
                  </p>
                </div>

                {/* Définition */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-2xl p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-blue-600 rounded-full p-3">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Définition</h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        Le <strong>Comité de Direction TI</strong> est l'instance stratégique qui assure l'<strong>alignement des investissements technologiques</strong> avec les objectifs d'affaires de l'organisation. 
                        Il agit comme <strong>pont décisionnel</strong> entre la direction exécutive et les équipes technologiques.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mission et Mandat */}
                <div>
                  <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Target className="w-7 h-7 text-green-600" />
                    Mission et Mandat
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white border-2 border-green-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-green-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <CheckCircle className="w-7 h-7 text-green-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Orienter les investissements TI</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Prioriser les projets technologiques selon leur <strong>valeur stratégique</strong>, leur <strong>retour sur investissement</strong> et leur <strong>alignement avec la vision d'affaires</strong>.
                      </p>
                    </div>

                    <div className="bg-white border-2 border-purple-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-purple-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <Shield className="w-7 h-7 text-purple-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Assurer la gouvernance et la conformité</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Veiller au respect des <strong>standards de l'industrie</strong> (ITIL, COBIT, NIST), de la <strong>cybersécurité</strong> et des <strong>réglementations</strong> applicables.
                      </p>
                    </div>

                    <div className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <TrendingUp className="w-7 h-7 text-blue-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Piloter la transformation numérique</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Identifier les <strong>opportunités d'innovation</strong>, les <strong>technologies émergentes</strong> (IA, cloud, automatisation) et orchestrer leur adoption stratégique.
                      </p>
                    </div>

                    <div className="bg-white border-2 border-orange-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-orange-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <AlertTriangle className="w-7 h-7 text-orange-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Gérer les risques technologiques</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Anticiper et <strong>mitiger les risques</strong> TI (sécurité, obsolescence, dépendances), assurer la <strong>continuité des opérations</strong> et la <strong>résilience</strong>.
                      </p>
                    </div>

                    <div className="bg-white border-2 border-pink-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-pink-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <Users className="w-7 h-7 text-pink-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Faciliter la collaboration client-experts</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Créer un <strong>langage commun</strong> entre parties prenantes business et techniques, clarifier les <strong>responsabilités</strong> (RACI) et éliminer les silos.
                      </p>
                    </div>

                    <div className="bg-white border-2 border-indigo-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
                      <div className="bg-indigo-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <Crown className="w-7 h-7 text-indigo-600" />
                      </div>
                      <h4 className="font-bold text-lg mb-3 text-gray-900">Optimiser la performance et le budget TI</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Suivre les <strong>KPIs opérationnels</strong> (SLA, incidents, satisfaction), contrôler les <strong>dépenses TI</strong> et maximiser la <strong>valeur de chaque dollar investi</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Standards de Gouvernance */}
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Shield className="w-7 h-7 text-purple-600" />
                    Standards de Gouvernance de Haut Niveau
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">📘</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">ITIL 4</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Gestion des services TI, amélioration continue, création de valeur client.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Standard mondial de facto</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">🔐</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">COBIT 2019</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Gouvernance TI/affaires, alignement stratégique, traçabilité pour audits.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Framework ISACA</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">🛡️</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">NIST Cybersecurity</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Gestion des risques cyber, conformité réglementaire, protection des données.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Norme US (référence mondiale)</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">📊</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">ISO 27001</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Système de gestion de la sécurité de l'information (SMSI), certification.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Norme ISO internationale</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">🎯</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">PMBOK (PMI)</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Gestion de projets, portefeuille, risques, parties prenantes, livrables.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Project Management Institute</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-purple-200">
                      <div className="text-4xl mb-3">⚡</div>
                      <h4 className="font-bold text-lg mb-2 text-gray-900">Agile / SAFe</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Agilité à l'échelle, livraison itérative, amélioration continue, feedback.
                      </p>
                      <div className="text-xs text-purple-600 font-semibold">Scaled Agile Framework</div>
                    </div>
                  </div>
                </div>

                {/* Composition et Fréquence */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Users className="w-6 h-6 text-blue-600" />
                      Composition Typique
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Président:</strong> Directeur Général ou VP client (Décisionnaire final)</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Prime/Directeur TI:</strong> Responsable livraison, stratégie TI, budget</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Experts BZ:</strong> Cybersécurité, IA, Cloud, Dev, Infrastructure</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Directeur Compte:</strong> Relation client, satisfaction, continuité</div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                      Cadence et Réunions
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Trimestrielle:</strong> Minimum pour pilotage stratégique (Q1, Q2, Q3, Q4)</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Durée:</strong> 90-120 minutes (efficacité et approfondissement)</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Ordre du jour structuré:</strong> KPIs, projets, risques, budget, innovation</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div><strong>Suivi actions:</strong> Registre décisions, responsables RACI, échéances</div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Citation inspirante */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">💡</div>
                  <blockquote className="text-2xl font-bold italic mb-4">
                    "Un Comité TI efficace transforme la technologie d'un centre de coûts en moteur stratégique de croissance."
                  </blockquote>
                  <p className="text-indigo-100">— Best practice de gouvernance TI moderne</p>
                </div>
              </div>
            )}

            {/* VUE D'ENSEMBLE */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Philosophie */}
                <div>
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Sparkles className="w-7 h-7 text-purple-600" />
                    Notre Philosophie de Gouvernance
                  </h2>
                  <div className="grid grid-cols-3 gap-6">
                    {philosophieGouvernance.map((philo) => (
                      <div key={philo.id} className={`${philo.couleur} p-6 rounded-2xl border-2 border-gray-100`}>
                        <div className="flex items-start gap-4">
                          {philo.icone === 'CheckCircle' && <CheckCircle className="w-8 h-8 flex-shrink-0" />}
                          {philo.icone === 'Target' && <Target className="w-8 h-8 flex-shrink-0" />}
                          {philo.icone === 'TrendingUp' && <TrendingUp className="w-8 h-8 flex-shrink-0" />}
                          <div>
                            <h3 className="font-bold text-lg mb-2">{philo.titre}</h3>
                            <p className="text-sm leading-relaxed">{philo.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl text-white">
                    <Users className="w-10 h-10 mb-3 opacity-80" />
                    <div className="text-4xl font-black mb-1">{statsGouvernance.partiesPrenantes}</div>
                    <div className="text-blue-100 font-medium">Parties Prenantes</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl text-white">
                    <Briefcase className="w-10 h-10 mb-3 opacity-80" />
                    <div className="text-4xl font-black mb-1">{statsGouvernance.membresStrategia}</div>
                    <div className="text-green-100 font-medium">Membres Stratégia Corp</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-2xl text-white">
                    <Layers className="w-10 h-10 mb-3 opacity-80" />
                    <div className="text-4xl font-black mb-1">{statsGouvernance.expertsBZ}</div>
                    <div className="text-purple-100 font-medium">Experts BZ</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-2xl text-white">
                    <Shield className="w-10 h-10 mb-3 opacity-80" />
                    <div className="text-2xl font-black mb-1">RACI</div>
                    <div className="text-orange-100 font-medium">Méthode Standard</div>
                  </div>
                </div>

                {/* Structure de Gouvernance */}
                <div>
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Users className="w-7 h-7 text-purple-600" />
                    Structure de Gouvernance
                  </h2>
                  <div className="flex flex-col items-center gap-4">
                    {/* Client Stratégia Corp */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-2xl w-96 text-center shadow-xl">
                      <Crown className="w-10 h-10 mx-auto mb-2" />
                      <h3 className="font-bold text-xl mb-1">CLIENT STRATÉGIA CORP</h3>
                      <p className="text-green-100 text-sm">Décisionnaire Final (Accountable)</p>
                    </div>
                    <div className="w-1 h-12 bg-gradient-to-b from-green-500 to-blue-500" />
                    
                    {/* Chargé Projet BZ */}
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6 rounded-2xl w-96 text-center shadow-xl">
                      <Target className="w-10 h-10 mx-auto mb-2" />
                      <h3 className="font-bold text-xl mb-1">CHARGÉ DE PROJET BZ</h3>
                      <p className="text-blue-100 text-sm">Gestion Direction TI • Exécution (Responsible)</p>
                    </div>
                    <div className="w-1 h-12 bg-gradient-to-b from-blue-500 to-orange-500" />

                    {/* Niveau inférieur */}
                    <div className="flex gap-6">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-2xl w-60 text-center shadow-xl">
                        <Briefcase className="w-10 h-10 mx-auto mb-2" />
                        <h3 className="font-bold text-xl mb-1">DIR. COMPTE</h3>
                        <p className="text-orange-100 text-sm">Relation Client</p>
                      </div>
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-2xl w-60 text-center shadow-xl">
                        <Sparkles className="w-10 h-10 mx-auto mb-2" />
                        <h3 className="font-bold text-xl mb-1">PRIME BZ</h3>
                        <p className="text-purple-100 text-sm">Conseiller Expert</p>
                      </div>
                      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white p-6 rounded-2xl w-60 text-center shadow-xl">
                        <Users className="w-10 h-10 mx-auto mb-2" />
                        <h3 className="font-bold text-xl mb-1">SPÉCIALISTES</h3>
                        <p className="text-pink-100 text-sm">Exécution Technique</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MATRICE RACI */}
            {activeTab === 'raci' && (
              <div className="space-y-8">
                {/* Légende RACI */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border-2 border-blue-200">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Lightbulb className="w-6 h-6 text-blue-600" />
                    Légende RACI
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl">R</div>
                      <div>
                        <div className="font-bold text-sm">Responsible</div>
                        <div className="text-xs text-gray-600">Fait le travail, exécute</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-green-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl">A</div>
                      <div>
                        <div className="font-bold text-sm">Accountable</div>
                        <div className="text-xs text-gray-600">Décide, approuve (1 seul!)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl">C</div>
                      <div>
                        <div className="font-bold text-sm">Consulted</div>
                        <div className="text-xs text-gray-600">Conseille avant action</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl">I</div>
                      <div>
                        <div className="font-bold text-sm">Informed</div>
                        <div className="text-xs text-gray-600">Informé après action</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table RACI */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-4 text-left font-bold">Activité</th>
                        <th className="border border-gray-300 p-4 text-center font-bold">Client</th>
                        <th className="border border-gray-300 p-4 text-center font-bold">Chargé Projet</th>
                        <th className="border border-gray-300 p-4 text-center font-bold">Prime</th>
                        <th className="border border-gray-300 p-4 text-center font-bold">Dir. Compte</th>
                        <th className="border border-gray-300 p-4 text-center font-bold">Spécialistes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(['Stratégique', 'Tactique', 'Opérationnel'] as const).map((niveau) => (
                        <>
                          <tr key={`header-${niveau}`}>
                            <td colSpan={6} className={`bg-gradient-to-r ${couleurNiveau[niveau]} text-white font-bold p-3 text-lg`}>
                              {niveau}
                            </td>
                          </tr>
                          {activitesParNiveau[niveau].map((activite) => (
                            <tr key={activite.id} className="hover:bg-gray-50 transition-colors">
                              <td className="border border-gray-300 p-4 font-medium">{activite.nom}</td>
                              <td className="border border-gray-300 p-4 text-center">{getRACIBadge(activite.client)}</td>
                              <td className="border border-gray-300 p-4 text-center">{getRACIBadge(activite.chargéProjet)}</td>
                              <td className="border border-gray-300 p-4 text-center">{getRACIBadge(activite.prime)}</td>
                              <td className="border border-gray-300 p-4 text-center">{getRACIBadge(activite.dirCompte)}</td>
                              <td className="border border-gray-300 p-4 text-center">{getRACIBadge(activite.spécialistes)}</td>
                            </tr>
                          ))}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Règle d'Or */}
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-2xl border-2 border-yellow-300">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-amber-900">
                    <Lightbulb className="w-6 h-6" />
                    Règle d'Or
                  </h3>
                  <p className="text-amber-900 leading-relaxed">
                    <span className="font-bold">Un seul "A" (Accountable) par activité!</span> C'est la personne qui a le dernier mot et qui assume la responsabilité finale. 
                    <span className="font-bold text-red-600"> Plusieurs "A" = confusion et conflits assurés!</span>
                  </p>
                </div>
              </div>
            )}

            {/* ÉQUIPE & RÔLES */}
            {activeTab === 'equipe' && (
              <div className="space-y-8">
                {/* Équipe Stratégia Corp */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Target className="w-7 h-7 text-green-600" />
                    Équipe Stratégia Corp
                  </h2>
                  <p className="text-gray-600 mb-6">Décisionnaires et parties prenantes client</p>
                  <div className="grid grid-cols-3 gap-6">
                    {equipeStrategia.map((membre) => (
                      <div key={membre.id} className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white w-16 h-16 rounded-xl flex items-center justify-center font-bold text-xl">
                            {membre.initiales}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-lg">{membre.prenom} {membre.nom}</h3>
                            <p className="text-sm text-gray-600 mb-2">{membre.titre}</p>
                            <span className={`${membre.badgeColor} text-white text-xs px-3 py-1 rounded-full font-medium`}>
                              {membre.badgeLabel}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <Mail className="w-4 h-4" />
                          {membre.email}
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Compétences</span>
                            <span className="font-bold text-purple-600">{membre.competenceScore}/100</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                              style={{ width: `${membre.competenceScore}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Équipe BZ */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Layers className="w-7 h-7 text-purple-600" />
                    Équipe BZ
                  </h2>
                  <p className="text-gray-600 mb-6">Experts et partenaires TI</p>
                  <div className="grid grid-cols-3 gap-6">
                    {equipeBZ.map((membre) => (
                      <div key={membre.id} className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-16 h-16 rounded-xl flex items-center justify-center font-bold text-xl">
                            {membre.initiales}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-lg">{membre.prenom} {membre.nom}</h3>
                            <p className="text-sm text-gray-600 mb-2">{membre.titre}</p>
                            <span className={`${membre.badgeColor} text-white text-xs px-3 py-1 rounded-full font-medium`}>
                              {membre.badgeLabel}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <Mail className="w-4 h-4" />
                          {membre.email}
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Compétences</span>
                            <span className="font-bold text-blue-600">{membre.competenceScore}/100</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all"
                              style={{ width: `${membre.competenceScore}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* GUIDE PÉDAGOGIQUE */}
            {activeTab === 'guide' && (
              <div className="space-y-8">
                {/* Pourquoi RACI */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 rounded-2xl border-2 border-blue-200">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Lightbulb className="w-7 h-7 text-blue-600" />
                    Pourquoi utiliser RACI?
                  </h2>
                  <p className="text-gray-700 mb-4 text-lg">
                    RACI est une méthode éprouvée qui élimine les zones grises dans la prise de décision. Pour Stratégia Corp, cela signifie:
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {beneficesRACI.map((benefice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                        <div>
                          <span className="font-bold text-gray-900">{benefice.titre}:</span>
                          <span className="text-gray-700"> {benefice.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exemple Concret */}
                <div>
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <BookOpen className="w-7 h-7 text-purple-600" />
                    Exemple Concret: {exempleConcret.titre}
                  </h2>

                  {/* Situation */}
                  <div className="bg-purple-50 p-6 rounded-2xl mb-6 border-2 border-purple-200">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      Situation
                    </h3>
                    <p className="text-gray-700">{exempleConcret.situation}</p>
                  </div>

                  {/* Rôles */}
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    {exempleConcret.roles.map((role, idx) => {
                      const couleurConfig = {
                        green: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', badge: 'bg-green-500' },
                        blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', badge: 'bg-blue-500' },
                        orange: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-500' },
                        purple: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', badge: 'bg-purple-500' }
                      };
                      const colors = couleurConfig[role.couleur as keyof typeof couleurConfig];
                      
                      return (
                        <div key={idx} className={`${colors.bg} p-6 rounded-2xl border-2 ${colors.border}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`${colors.badge} text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl`}>
                              {role.lettre}
                            </div>
                            <div>
                              <h3 className={`font-bold text-lg ${colors.text}`}>{role.personne} ({role.titre})</h3>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {role.actions.map((action, aidx) => (
                              <div key={aidx} className="flex items-start gap-2">
                                <CheckCircle className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                                <span className="text-sm text-gray-700">{action}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Résultat */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border-2 border-green-300">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-green-700">
                      <Sparkles className="w-6 h-6" />
                      Résultat
                    </h3>
                    <p className="text-gray-700 text-lg leading-relaxed">{exempleConcret.resultat}</p>
                  </div>
                </div>

                {/* Pièges à Éviter */}
                <div className="bg-gradient-to-r from-red-50 to-rose-50 p-8 rounded-2xl border-2 border-red-200">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-red-700">
                    <AlertTriangle className="w-7 h-7" />
                    Pièges à Éviter
                  </h2>
                  <div className="space-y-4">
                    {piegesRACI.map((piege, idx) => (
                      <div key={idx} className="bg-white p-5 rounded-xl border-2 border-red-200">
                        <h3 className="font-bold text-lg mb-2 text-red-700 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          {piege.titre}
                        </h3>
                        <p className="text-gray-700 mb-2">{piege.description}</p>
                        <p className="text-green-700 font-semibold">{piege.solution}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notre Engagement */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 rounded-2xl text-white">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <UserCheck className="w-7 h-7" />
                    Notre Engagement
                  </h2>
                  <div className="space-y-3">
                    {engagementBZ.map((engagement, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                        <span className="text-lg">{engagement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default GouvernanceComiteModal;

